import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, Loader2, Check, ArrowRight } from 'lucide-react';
import { API } from '../../api';

const LABELS = { does: 'What you do', serves: 'Who you serve', different: 'What makes you different', tone: 'How you talk' };

// Public teaser of the AI website reader — calls the real LLM-backed endpoint.
export default function WebsiteReader() {
  const nav = useNavigate();
  const [url, setUrl] = useState('');
  const [state, setState] = useState('idle'); // idle | reading | done
  const [fields, setFields] = useState(null);

  const read = async () => {
    setState('reading'); setFields(null);
    try {
      const data = await API.readSite(url);
      const p = data.profile || {};
      setFields({ does: p.does, serves: p.serves, different: p.different, tone: p.tone });
      localStorage.setItem('bm_reader', JSON.stringify(data));
      setState('done');
    } catch (e) {
      setState('idle');
    }
  };

  return (
    <section className="relative py-28 bg-ink">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
        <div className="reveal">
          <p className="eyebrow mb-4">Skip the paperwork</p>
          <h2 className="font-display text-5xl md:text-6xl text-cream leading-[0.95]">Paste your website. <span className="italic text-gold-grad">We&rsquo;ll take it from there.</span></h2>
          <p className="mt-6 text-cream/65 text-lg leading-relaxed max-w-lg">We read your site and fill in your business profile: what you do, who you serve, what makes you different. You check it, change anything, and send it.</p>
          <p className="mt-4 text-cream/45 italic font-display text-xl">The reading is automated. The song never is.</p>
        </div>

        <div className="reveal panel rounded-3xl p-7">
          <div className="flex items-center gap-3 rounded-full border hairline px-4 py-2 focus-within:border-gold/50 transition-colors">
            <Globe size={18} className="text-gold shrink-0" />
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="yourbusiness.com"
              className="bg-transparent flex-1 outline-none text-cream placeholder:text-cream/35 py-1" />
            <button onClick={read} disabled={state === 'reading'}
              className="btn-gold rounded-full px-4 py-1.5 text-sm flex items-center gap-2 disabled:opacity-70">
              {state === 'reading' ? <><Loader2 size={15} className="animate-spin" /> Reading</> : <>Read my site</>}
            </button>
          </div>

          <div className="mt-6 space-y-3 min-h-[180px]">
            {state === 'idle' && <p className="text-cream/40 text-sm py-10 text-center">Leave it blank or type any address to watch the profile fill in.</p>}
            {state === 'reading' && [0,1,2,3].map((i) => (
              <div key={i} className="h-14 rounded-xl bg-panel2 animate-pulse" style={{ animationDelay: `${i*120}ms` }} />
            ))}
            {state === 'done' && fields && Object.entries(fields).filter(([, v]) => v).map(([k, v], i) => (
              <div key={k} className="reveal in rounded-xl border hairline p-3.5 bg-panel2/50" style={{ transitionDelay: `${i*90}ms` }}>
                <div className="flex items-center gap-2 mb-1"><Check size={13} className="text-gold" /><span className="eyebrow text-[9px]">{LABELS[k]}</span></div>
                <p className="text-cream/90 text-sm">{v}</p>
              </div>
            ))}
          </div>

          {state === 'done' && (
            <button onClick={() => nav('/start')} className="mt-5 w-full btn-gold rounded-full py-3 flex items-center justify-center gap-2">
              Looks right &mdash; continue <ArrowRight size={17} />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
